import type { HiredEquipmentGridRow } from '../types';

export type RequiredGridField = 'date' | 'task' | 'vendor' | 'resource' | 'qty' | 'rate';

/** "ea" / "each" (any punctuation/case) — count units, whole numbers only. */
export function isEachUnit(unit: string | null | undefined): boolean {
  const normalized = (unit ?? '').trim().toLowerCase().replace(/[.\s]/g, '');
  return normalized === 'ea' || normalized === 'each';
}

export function qtyStepForUnit(unit: string | null | undefined): string {
  return isEachUnit(unit) ? '1' : '0.01';
}

function isNonIntegerQty(qty: number): boolean {
  return !Number.isInteger(qty);
}

export function isRequiredFieldMissing(row: HiredEquipmentGridRow, field: RequiredGridField): boolean {
  switch (field) {
    case 'date':
      return !row.date;
    case 'task':
      return row.taskId == null;
    case 'vendor':
      return row.vendorId == null;
    case 'resource':
      // A lump sum has no resource - "lump sum" IS the resource.
      if (row.lumpSum) return false;
      return row.resourceId == null && !row.resourceLabel;
    case 'qty':
      // A lump sum has no qty; it is normalized to 1 so the amount math works.
      if (row.lumpSum) return false;
      if (row.qty == null || Number.isNaN(row.qty)) return true;
      return isEachUnit(row.unit) && isNonIntegerQty(row.qty);
    case 'rate':
      if (row.rate == null || Number.isNaN(row.rate)) return true;
      // On a lump sum this cell is the amount. $0 would be dropped silently at
      // approve (extractHiredEquipmentData returns null), so require a real figure.
      return row.lumpSum && row.rate <= 0;
  }
}

export function rowHasMissingRequired(row: HiredEquipmentGridRow): boolean {
  return (
    isRequiredFieldMissing(row, 'date') ||
    isRequiredFieldMissing(row, 'task') ||
    isRequiredFieldMissing(row, 'vendor') ||
    isRequiredFieldMissing(row, 'resource') ||
    isRequiredFieldMissing(row, 'qty') ||
    isRequiredFieldMissing(row, 'rate')
  );
}

export interface GridValidationResult {
  ok: boolean;
  /** 0-based indexes of incomplete rows. Empty grid → []. */
  invalidIndexes: number[];
  /** 1-based line numbers matching the grid # column. */
  invalidLineNumbers: number[];
  invalidCount: number;
  firstInvalidIndex: number;
}

export function validateGridRows(rows: HiredEquipmentGridRow[]): GridValidationResult {
  if (rows.length === 0) {
    return {
      ok: false,
      invalidIndexes: [],
      invalidLineNumbers: [],
      invalidCount: 0,
      firstInvalidIndex: -1,
    };
  }
  const invalidIndexes = rows.reduce<number[]>((acc, row, index) => {
    if (rowHasMissingRequired(row)) acc.push(index);
    return acc;
  }, []);
  const invalidLineNumbers = invalidIndexes.map((index) => index + 1);
  return {
    ok: invalidIndexes.length === 0,
    invalidIndexes,
    invalidLineNumbers,
    invalidCount: invalidIndexes.length,
    firstInvalidIndex: invalidIndexes[0] ?? -1,
  };
}

/** English list: "2", "2 and 4", "1, 3, and 5". */
export function formatLineNumberList(lineNumbers: number[]): string {
  if (lineNumbers.length === 0) return '';
  if (lineNumbers.length === 1) return String(lineNumbers[0]);
  if (lineNumbers.length === 2) return `${lineNumbers[0]} and ${lineNumbers[1]}`;
  return `${lineNumbers.slice(0, -1).join(', ')}, and ${lineNumbers[lineNumbers.length - 1]}`;
}

export function formatIncompleteLinesMessage(lineNumbers: number[]): string {
  const count = lineNumbers.length;
  if (count === 0) return '';
  const list = formatLineNumberList(lineNumbers);
  if (count === 1) {
    return `1 incomplete line — line ${list} is missing required fields.`;
  }
  return `${count} incomplete lines — lines ${list} are missing required fields.`;
}

export function gridLineElementId(lineNumber: number): string {
  return `he-grid-line-${lineNumber}`;
}

export function scrollToGridLine(lineNumber: number): void {
  document.getElementById(gridLineElementId(lineNumber))?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
}
